import { useEffect, useState } from 'react'

import { ActionIcon, Collapse, TextInput } from '@mantine/core'
import { Check, X } from 'lucide-react'

import { useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import { isAdminOrOps } from 'src/lib/auth/permissions'
import DNButton from 'src/components/Buttons/Button/DNButton'
import StyledReadOnlyInput from 'src/components/Inputs/StyledReadOnlyInput/StyledReadOnlyInput'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_COMPANY,
  UPDATE_COMPANY,
} from 'src/graphql/companies.graphql'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconEdit from 'src/icons/IconEdit'

import FieldLabel from './FieldLabel'

interface AdverseActionsCardProps {
  companyData: {
    companyId: string
    preAdverseActionDelayHours: number | null
    finalAdverseActionDelayHours: number | null
  }
  initialOpened?: boolean
}

const AdverseActionsCard = ({
  companyData,
  initialOpened = false,
}: AdverseActionsCardProps) => {
  const { currentUser } = useAuth()
  const isAdmin = isAdminOrOps(currentUser)
  const isRecruiter = currentUser?.roles?.includes('RECRUITER')
  const canEdit = isAdmin || isRecruiter

  const [values, setValues] = useState({ ...companyData })
  const [editing, setEditing] = useState(false)
  const [opened, setOpened] = useState(initialOpened)

  const [updateCompany, { loading }] = useMutation(UPDATE_COMPANY, {
    onCompleted: () => {
      toast('Adverse action settings updated successfully', 'success')
      setEditing(false)
    },
    onError: (error) => {
      toast(`Failed to update settings: ${error.message}`, 'error')
    },
    refetchQueries: [
      {
        query: GET_COMPANY,
        variables: { id: values.companyId },
      },
    ],
    awaitRefetchQueries: true,
  })

  // Sync internal state with parent data changes only when not editing
  useEffect(() => {
    if (!editing) {
      setValues({ ...companyData })
    }
  }, [companyData, editing])

  const handleEdit = () => {
    setEditing(true)
  }

  const handleCancel = () => {
    setValues({ ...companyData })
    setEditing(false)
  }

  const handleSave = async () => {
    const { companyId, ...rest } = values

    await updateCompany({
      variables: {
        id: companyId,
        input: rest,
      },
    })
  }

  const preAdverseActionDelayLabel = (
    <FieldLabel
      label="Pre-Adverse Action Delay"
      tooltip={
        <>
          The number of hours to wait before sending a pre-adverse action notice
          to an applicant.
          <br />
          <br />
          This delay allows time for applicants to respond or dispute information
          before final adverse action is taken.
          <br />
          <br />
          Default value: 24 hours
        </>
      }
    />
  )

  const finalAdverseActionDelayLabel = (
    <FieldLabel
      label="Final Adverse Action Delay"
      tooltip={
        <>
          The number of hours to wait after sending a pre-adverse action notice
          before sending the final adverse action notice.
          <br />
          <br />
          This delay provides applicants with sufficient time to respond to the
          pre-adverse action notice.
          <br />
          <br />
          Default value: 120 hours
        </>
      }
    />
  )

  return (
    <div
      className={`flex w-full flex-col gap-2 rounded-lg border border-doubleNickel-gray-200 p-3`}
    >
      <div
        className="flex w-full cursor-pointer flex-row items-center gap-2 text-sm font-semibold text-doubleNickel-gray-900"
        onClick={() => setOpened((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpened((prev) => !prev)
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={opened}
      >
        <ActionIcon variant="subtle" className="pointer-events-none">
          <IconChevronDown
            className={`h-4 w-4 ${
              !opened && '-rotate-90'
            } fill-none stroke-doubleNickel-gray-500 transition-all`}
          />
        </ActionIcon>
        Adverse Actions
        {canEdit && !editing && (
          <DNButton
            variant="transparent"
            onClick={(e) => {
              e.stopPropagation()
              if (!opened) setOpened(true)
              handleEdit()
            }}
            className="ml-auto text-doubleNickel-gray-700"
          >
            <IconEdit className="h-3.5 w-3.5 fill-none stroke-current" />
            Edit
          </DNButton>
        )}
        {canEdit && editing && (
          <div className="ml-auto flex items-center gap-2">
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleSave()
              }}
              disabled={loading}
              className="text-doubleNickel-success-500"
            >
              <Check size={14} />
              Save
            </DNButton>
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleCancel()
              }}
              disabled={loading}
              className="text-doubleNickel-error-500"
            >
              <X size={14} />
              Cancel
            </DNButton>
          </div>
        )}
      </div>
      <Collapse in={opened} transitionDuration={400}>
        <div className="flex flex-col gap-2">
          <div className={`grid grid-cols-12 gap-3`}>
            {/* Pre-Adverse Action Delay */}
            <div className="col-span-3">
              {editing ? (
                <TextInput
                  label={preAdverseActionDelayLabel}
                  value={values.preAdverseActionDelayHours ?? ''}
                  onChange={(e) => {
                    if (e.target.value === '') {
                      setValues({
                        ...values,
                        preAdverseActionDelayHours: 0,
                      })
                    } else {
                      const value = parseInt(e.target.value)
                      if (!isNaN(value)) {
                        setValues({
                          ...values,
                          preAdverseActionDelayHours: value,
                        })
                      }
                    }
                  }}
                  placeholder="hours"
                  className="w-full"
                  type="number"
                  min={0}
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label={preAdverseActionDelayLabel}
                  value={
                    values.preAdverseActionDelayHours !== null &&
                    values.preAdverseActionDelayHours !== undefined
                      ? `${values.preAdverseActionDelayHours} ${
                          values.preAdverseActionDelayHours === 0 ||
                          values.preAdverseActionDelayHours === 1
                            ? 'hour'
                            : 'hours'
                        }`
                      : '-'
                  }
                />
              )}
            </div>

            {/* Final Adverse Action Delay */}
            <div className="col-span-3">
              {editing ? (
                <TextInput
                  label={finalAdverseActionDelayLabel}
                  value={values.finalAdverseActionDelayHours ?? ''}
                  onChange={(e) => {
                    if (e.target.value === '') {
                      setValues({
                        ...values,
                        finalAdverseActionDelayHours: 0,
                      })
                    } else {
                      const value = parseInt(e.target.value)
                      if (!isNaN(value)) {
                        setValues({
                          ...values,
                          finalAdverseActionDelayHours: value,
                        })
                      }
                    }
                  }}
                  placeholder="hours"
                  className="w-full"
                  type="number"
                  min={0}
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label={finalAdverseActionDelayLabel}
                  value={
                    values.finalAdverseActionDelayHours !== null &&
                    values.finalAdverseActionDelayHours !== undefined
                      ? `${values.finalAdverseActionDelayHours} ${
                          values.finalAdverseActionDelayHours === 0 ||
                          values.finalAdverseActionDelayHours === 1
                            ? 'hour'
                            : 'hours'
                        }`
                      : '-'
                  }
                />
              )}
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  )
}

export default AdverseActionsCard

